import { Box, Grid } from '@mui/material'

import { ToastContainer, toast } from 'react-toastify'
import NotasTable from '~components/table/notasTable'
import React, { useEffect, useState, useCallback } from 'react'
import ModalCobrar from '~components/modais/modalCobrar'
import ModalDownloadInvoices from '~components/modais/modalDownloadInvoices'
import { useUserStore } from 'stores/users'
import 'dayjs/locale/pt-br'
import ReceiptIcon from '@mui/icons-material/Receipt'
import { colors } from '~constants/colorsPallete'
import MiniDrawer from '~components/drawer'
import Header from '~components/header'
import { SendSlackMessage } from '~services/Slack'
import { GetFilteredTaxNotes, ChargeTaxNotes, ExportTaxNotes } from '~services/Receipts'
import { useLocation, useNavigate } from 'react-router-dom'
import ReceiptsFilters from './filters'
import { InvoiceFilters } from './filters'

function Nfs() {
  const [pageInfo, setPageInfo] = useState({ page: '1', per_page: '10' })
  const [count, setCount] = React.useState<number>(0)
  const [loading, setLoading] = useState(false)
  const Uid = useUserStore((state) => state.phone)
  const access = useUserStore((state) => state.token)
  const client = useUserStore((state) => state.client)
  const [data, setData] = useState<[]>([])
  const { openDrawer } = useUserStore()
  const location = useLocation()
  const navigate = useNavigate()
  const from = location.state?.from?.pathname || '/'
  const clearUser = useUserStore((state) => state.clearUser)
  const loggedIn = useUserStore((state) => state.setLogged)
  const [filters, setFilters] = useState<InvoiceFilters>({
    start_competence_date: '',
    end_competence_date: '',
    invoice_status: '',
    companyName: '',
    operator_status: '',
    business_nickname: '',
    business_name: '',
    order_by: 'competence_date',
    order_direction: 'desc',
  })
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([])
  const [openCobrarModal, setOpenCobrarModal] = useState(false)
  const [openDownloadModal, setOpenDownloadModal] = useState(false)

  const handleFilterChange = (newFilters: InvoiceFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }))
    setPageInfo((prev) => ({ page: '1', per_page: String(prev.per_page) }))
  }

  // Sorting changes coming from NotasTable
  const handleTableSortChange = (sort: { order_by: string; order_direction: 'asc' | 'desc' }) => {
    setFilters((prev) => ({ ...prev, ...sort }))
    // Reset to first page when sorting changes
    setPageInfo((prev) => ({ page: '1', per_page: String(prev.per_page) }))
  }

  const handleSelectionChange = useCallback((selectedIds: string[]) => {
    // console.log('Parent: Selection changed to:', selectedIds)
    setSelectedInvoiceIds(selectedIds)
  }, [])

  const handleCobrarClick = () => {
    if (selectedInvoiceIds.length === 0) {
      toast.warning('Selecione pelo menos uma nota fiscal')
      return
    }
    setOpenCobrarModal(true)
  }

  const handleDownloadClick = () => {
    if (selectedInvoiceIds.length === 0) {
      toast.warning('Selecione pelo menos uma nota fiscal')
      return
    }

    // Check if all selected invoices have PDF files
    const selectedInvoices = data.filter((item: any) => selectedInvoiceIds.includes(item.hashid))
    const invoicesWithoutPdf = selectedInvoices.filter((item: any) => !item?.invoice?.pdf_file_url)

    if (invoicesWithoutPdf.length > 0) {
      if (invoicesWithoutPdf.length === selectedInvoiceIds.length) {
        toast.error('Nenhuma das notas fiscais selecionadas possui arquivo PDF disponível para download.')
      } else {
        toast.warning(
          `${invoicesWithoutPdf.length} de ${selectedInvoiceIds.length} nota(s) fiscal(is) selecionada(s) não possui(em) arquivo PDF disponível.`,
        )
      }
      return
    }

    setOpenDownloadModal(true)
  }

  const handleCobrarConfirm = async (ids: string[]) => {
    try {
      await ChargeTaxNotes(ids)

      // Success is handled by the modal with toast
      // Optionally refresh the data to show updated status
      fetchData(pageInfo)
    } catch (error) {
      console.error('Error charging tax notes:', error)
      throw error // Re-throw so modal can show error toast
    }
  }

  const handleDownloadConfirm = async (ids: string[]) => {
    try {
      const response = await ExportTaxNotes(ids)

      // response.data is already a Blob from the API (responseType: 'blob')
      const blob = new Blob([response.data], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0]
      link.setAttribute('download', `notas_fiscais_${timestamp}.zip`)

      document.body.appendChild(link)
      link.click()

      // Cleanup
      link.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading tax notes:', error)
      throw error // Re-throw so modal can show error toast
    }
  }

  const fetchData = useCallback(
    (pageInfo: { per_page: string; page: string }) => {
      setLoading(true)
      const params: Record<string, string> = {}

      // console.log('Filters applied:', filters)
      // Do a for each on filters to add to params if not empty. This will all valid values
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          params[key] = value
        }
      })
      // console.log('Params for API call:', params)

      setData([])

      GetFilteredTaxNotes(Uid, access, client, pageInfo, params)
        .then((response) => {
          setData(response.data.data)
          setCount(response.data.meta.total_count)

          setLoading(false)
        })
        .catch((error) => {
          setLoading(false)
          console.log(error)
          if (error.request?.status === 401) {
            loggedIn(false)
            clearUser()
            navigate(from, { replace: true })
          }
          toast.error('Ooops! Algo deu errado, tente novamente!')
          SendSlackMessage(
            `/shared_api/v1/tax_note_stats?per_page=${pageInfo.per_page}&page=${pageInfo.page}`,
            error,
            params,
            Uid,
            'Get',
          )
        })
    },
    [Uid, access, client, filters, loggedIn, clearUser, navigate, from],
  )

  useEffect(() => {
    fetchData(pageInfo)
  }, [pageInfo, fetchData])

  const handleTablePageChange = (zeroBasedPage: number) => {
    setPageInfo((prev) => ({ ...prev, page: String(zeroBasedPage + 1) }))
  }

  const handleTableRowsPerPageChange = (rows: number) => {
    setPageInfo({ page: '1', per_page: String(rows) })
  }

  return (
    <>
      <ModalCobrar
        open={openCobrarModal}
        handleClose={() => setOpenCobrarModal(false)}
        selectedIds={selectedInvoiceIds}
        onConfirm={handleCobrarConfirm}
      />
      <ModalDownloadInvoices
        open={openDownloadModal}
        handleClose={() => setOpenDownloadModal(false)}
        selectedIds={selectedInvoiceIds}
        onConfirm={handleDownloadConfirm}
      />
      <Box sx={{ backgroundColor: colors.bg }}>
        <ToastContainer />
        <Grid container spacing={2} p={2} columns={16}>
          <Grid item xs={2} sm={openDrawer ? 3 : 1} md={openDrawer ? 3 : 1}>
            <MiniDrawer />
          </Grid>
          <Grid item xs={14} sm={openDrawer ? 13 : 15} md={openDrawer ? 13 : 15}>
            <Header text="Notas Fiscais" icon={<ReceiptIcon sx={{ mt: '4px', mr: 2, color: colors['gray.500'] }} />} />
            <ReceiptsFilters
              onFilterChange={handleFilterChange}
              selectedInvoiceIds={selectedInvoiceIds}
              onCobrarClick={handleCobrarClick}
              onDownloadClick={handleDownloadClick}
            />
            {selectedInvoiceIds.length > 0 && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(0, 157, 129, 0.1)', borderRadius: 1 }}>
                <strong>{selectedInvoiceIds.length}</strong> nota(s) fiscal(is) selecionada(s)
              </Box>
            )}
            <NotasTable
              data={data}
              loading={loading}
              count={count}
              onSelectionChange={handleSelectionChange}
              page={Number(pageInfo.page) - 1}
              rowsPerPage={Number(pageInfo.per_page)}
              onPageChange={handleTablePageChange}
              onRowsPerPageChange={handleTableRowsPerPageChange}
              onSortChange={handleTableSortChange}
              currentOrderBy={filters.order_by}
              currentOrderDirection={filters.order_direction}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export default Nfs
